import { useEffect, useState } from "react";
import { RequiredLabel } from "../../component/requiredLabel/RequiredLabel";
import TextInput from "../../component/textInput/TextInput";
import RichTextInput from "../../component/richTextInput/RichTextInput";
import {
  PrimaryButton,
  SecondaryButton,
} from "../../component/buttonComponent/ButtonComponent";
import CustomDatePicker from "../../component/datePicker/DatePicker";

const isEmpty = (v) => !v || v.trim() === "";

export const BusPolicyForm = ({
  mode,
  existingData,
  onSubmit,
  handleReset,
  loading,
  input,
  setInput,
  error,
  setError,
}) => {
  const [isSubmitDisabled, setIsSubmitDisabled] = useState(true);
  const [initialInput, setInitialInput] = useState(true);
  const [keyUp, setKeyUp] = useState("", false);
  const [isMobileViewport, setIsMobileViewport] = useState(
    typeof window !== "undefined" ? window.innerWidth <= 768 : false
  );

  const isBusPolicyValid = () => {
    if (!input) return false;

    const versionRegex = /^[0-9.]+$/;

    const isValid =
      input.title.trim() !== "" &&
      input.version.trim() !== "" &&
      versionRegex.test(input.version) &&
      input.content.trim() !== "";

    const isEdited =
      initialInput && JSON.stringify(initialInput) !== JSON.stringify(input);

    return isValid && isEdited;
  };

  // Update error state
  useEffect(() => {
    const versionRegex = /^[0-9.]+$/;

    setError({
      title: input.title !== "" ? isEmpty(input.title) : false,
      version: input.version !== "" ? !versionRegex.test(input.version) : false,
      content: false,
      effectiveDate: false,
    });
  }, [input]);

  useEffect(() => {
    setIsSubmitDisabled(!isBusPolicyValid());
  }, [input, initialInput]);

  useEffect(() => {
    const handleResize = () => setIsMobileViewport(window.innerWidth <= 768);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    const updatedInput = { ...input, [name]: value };
    setInput(updatedInput);
  };

  useEffect(() => {
    if ((mode === "edit" || mode === "view") && existingData) {
      const { _id, institutionId, createdAt, updatedAt, ...rest } =
        existingData;

      setInitialInput(rest);
      setInput(rest);
    } else {
      setInitialInput(input);
    }
  }, [mode, existingData]);

  const fullWidth = "100%";
  const textFieldWidth = isMobileViewport ? fullWidth : "45rem";
  const contentWidth = isMobileViewport ? fullWidth : "47rem";
  const datePickerWidth = isMobileViewport ? fullWidth : "22rem";

  return (
    <>
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          height: isMobileViewport ? "calc(100dvh - 3.5rem)" : "80vh",
        }}
      >
        <div
          style={{
            flex: 1,
            overflowY: "auto",
            paddingRight: isMobileViewport ? 0 : "1rem",
            boxSizing: "border-box",
          }}
        >
          <div className="name-form-container">
            <div className="input-label">
              <RequiredLabel>Policy Title</RequiredLabel>
              <div className="input-description">
                Enter the title of your bus policy.
              </div>
            </div>
            <div className="input-container">
              <TextInput
                placeholder={mode === "view" ? input.title : "Enter Title"}
                setInput={setInput}
                input={mode === "view" ? "" : input}
                name="title"
                disable={mode === "view"}
                type="text"
                error={error.title}
                setError={setError}
                errorMessage=""
                keyUp={keyUp}
                setKeyUp={setKeyUp}
                onChangeFunction={handleInputChange}
                width={textFieldWidth}
              />
            </div>
          </div>
          <div className="name-form-container">
            <div className="input-label">
              <RequiredLabel>Policy Version</RequiredLabel>
              <div className="input-description">
                Enter a version number for your policy.
              </div>
            </div>
            <div className="input-container">
              <TextInput
                placeholder={mode === "view" ? input.version : "Enter Version"}
                setInput={setInput}
                input={mode === "view" ? "" : input}
                name="version"
                disable={mode === "view"}
                type="text"
                error={error.version}
                setError={setError}
                errorMessage="Please only use numbers and dots"
                keyUp={keyUp}
                setKeyUp={setKeyUp}
                onChangeFunction={handleInputChange}
                width={textFieldWidth}
              />
            </div>
          </div>
          <div className="name-form-container">
            <div className="input-label">
              Effective Date
              <div className="input-description">
                Fill in this date field if you would like to specify a date to
                publish this policy.
              </div>
            </div>
            <div className="input-container">
              <div
                style={{
                  display: "flex",
                  alignItems: isMobileViewport ? "stretch" : "flex-end",
                  gap: "0.75rem",
                  flexDirection: isMobileViewport ? "column" : "row",
                  width: "100%",
                }}
              >
                <CustomDatePicker
                  disable={mode === "view" || mode === "edit"}
                  placeholder="Select a date"
                  name="effectiveDate"
                  error={mode === "view"}
                  setError={setError}
                  input={input}
                  setInput={setInput}
                  errorMessage=""
                  width={datePickerWidth}
                  disablePast={true}
                />

                <SecondaryButton
                  width={isMobileViewport ? datePickerWidth : "6rem"}
                  label="Clear"
                  imageIcon={false}
                  disable={!input.effectiveDate || mode === "view" || mode === "edit"}
                  onClickFunction={() =>
                    setInput((prev) => ({
                      ...prev,
                      effectiveDate: "",
                    }))
                  }
                />
              </div>
            </div>
          </div>
          <div
            className="name-form-container"
            style={{
              height: isMobileViewport ? "auto" : "15rem",
              borderBottom: "none",
              alignItems: isMobileViewport ? "stretch" : "flex-start",
            }}
          >
            <div className="input-label">
              <RequiredLabel>Policy Content</RequiredLabel>
              <div className="input-description">
                Enter the content of the policy here
              </div>
            </div>
            <div
              className="input-container"
              style={{ width: isMobileViewport ? "100%" : "auto" }}
            >
              <RichTextInput
                value={input.content}
                onChange={(value) =>
                  setInput((prev) => ({ ...prev, content: value }))
                }
                placeholder=""
                width={contentWidth}
                readOnly={mode === "view"}
                error={error.content}
              />
            </div>
          </div>
        </div>
        <div
          style={{
            display: "flex",
            gap: "1rem",
            padding: isMobileViewport
              ? "1.5rem 0.75rem 0.75rem"
              : "1rem",
            justifyContent: isMobileViewport ? "center" : "flex-end",
            borderTop: "1px solid #EAECF0",
            width: "100%",
            boxSizing: "border-box",
            flexDirection: "row",
            alignItems: "center",
          }}
        >
          <SecondaryButton
            width="6rem"
            label="Cancel"
            imageIcon={false}
            onClickFunction={handleReset}
          />
          {mode !== "view" && (
            <PrimaryButton
              width="6rem"
              disable={isSubmitDisabled}
              label={mode === "create" ? "Create" : "Save"}
              onClickFunction={onSubmit}
              loading={loading}
            />
          )}
        </div>
      </div>
    </>
  );
};
